import { Box } from "@mui/material";
import {
  type StoriesParams,
  useStoriesQuery,
} from "Api/Queries/Stories/useStoriesQuery";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StoryListTable } from "Components/Stories/Management/StoryListTable";
import type {
  StoryListExtraColumnId,
  StoryListHandlers,
} from "Components/Stories/Management/storyListColumns";
import { useServerTable } from "Hooks/useServerTable";
import { type StoryListItemDto, StoryState } from "Infrastructure/Api/Api";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { asEnumValue, asString } from "Utils/FilterUtils";

const RESOURCES = Resources.Stories.Management;

const DEFAULT_SORTING = [{ id: "dateCreated", desc: true }] as const;

const buildTableParams = (
  queryParams: ReturnType<
    typeof useServerTable<StoryListItemDto, StoryListExtraColumnId>
  >["queryParams"],
): StoriesParams => ({
  offset: queryParams.offset,
  limit: queryParams.limit,
  sortBy: queryParams.sortBy,
  sortDirection: queryParams.sortDirection,
  authorName: asString(queryParams.filters.authorName),
  text: asString(queryParams.filters.text),
  state: asEnumValue(StoryState, queryParams.filters.state),
});

/** The Admin list: server-paged, sortable and filterable across all authors. */
export const AdminStoriesTable = ({
  isPending,
  onPreview,
  onPublish,
  onUnpublish,
  onDelete,
}: StoryListHandlers) => {
  const { t } = useResource();
  const table = useServerTable<StoryListItemDto, StoryListExtraColumnId>({
    defaultSorting: [...DEFAULT_SORTING],
  });
  const params = useMemo(
    () => buildTableParams(table.queryParams),
    [table.queryParams],
  );
  const query = useStoriesQuery(params);

  if (query.isError) {
    return <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>;
  }

  return (
    <Box display="grid" alignItems="start">
      <StoryListTable
        data={query.data?.items ?? []}
        isLoading={query.isLoading}
        isRefetching={query.isRefetching}
        rowCount={query.data?.totalCount ?? 0}
        tableProps={table.tableProps}
        isPending={isPending}
        onPreview={onPreview}
        onPublish={onPublish}
        onUnpublish={onUnpublish}
        onDelete={onDelete}
      />
    </Box>
  );
};
